/** @jsxRuntime classic */
/** @jsx jsxCustomEvent */
import jsxCustomEvent from '@micro-zoe/micro-app/polyfill/jsx-custom-event';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuIdv4 } from 'uuid';
import { IMicroAppContainerProps, MicroActType } from './interface';
import microApp, { EventCenterForMicroApp } from '@micro-zoe/micro-app';
import { $window, updateUrlParameter } from '@umu/umu-dom';
import camelCase from 'lodash/camelCase';
import { Loading } from 'common/src/components';
import isEqual from 'lodash/isEqual';
import { onDealHaUrl } from './dataFlow';
interface IMicroAppContent {
    addDataListener: (cb: CallableFunction, autoTrigger?: boolean) => void;
    clearDataListener: () => void;
}

const MicroEventKey = 'MicroAppEventCenter';
const MicroAppContainer = (props: IMicroAppContainerProps) => {
    const {
        microAppProps,
        info,
        className = '',
        isFirstInit = false,
        style = {},
        loadElement,
        onCommunication,
    } = props;
    const [isLoading, setIsLoading] = useState(true);
    const refInfo = useRef(info);
    const { name, url, props: microProps } = microAppProps;
    const refChildPageId = useRef<string>('');
    const refAppKey = useRef<string>(camelCase(uuIdv4()));
    const appName = `${name}${refAppKey.current}`;
    const eventKey = `${appName}${MicroEventKey}`;
    ($window as any)[eventKey] = new EventCenterForMicroApp(appName);
    if (!microApp.hasInit) {
        microApp.start({
            'disable-memory-router': true,
        });
    }
    useEffect(() => {
        if (!isEqual(refInfo.current, info)) {
            microApp.setData(appName, info as any);
        }
    }, [info]);
    const onInit = () => {
        (microApp as any)?.removeDataListener?.(appName);
        microApp.addDataListener(
            appName,
            (data: { act: MicroActType; linkId: string; pageId: string; [key: string]: any }) => {
                if (data?.act === MicroActType.FIRST_INIT && refChildPageId.current === '') {
                    refChildPageId.current = data?.linkId;
                    isFirstInit && setIsLoading(false);
                    return {
                        ...info,
                    };
                }
                if (refChildPageId.current !== data.pageId) {
                    return {
                        reject: true,
                    };
                }
                const result = onCommunication?.(MicroActType.UPDATE_DATA, data);
                return {
                    success: true,
                    result,
                };
            },
        );
    };

    useEffect(() => {
        onInit();
        return () => {
            ($window as any)[eventKey] = null;
        };
    }, []);
    const onMounted = (e: { detail: any }) => {
        const { detail } = e;
        if (detail?.name !== appName) {
            return;
        }
        if (!isFirstInit) {
            setIsLoading(false);
        }
        onCommunication?.(MicroActType.AFTER_MOUNT, e);
    };
    const renderLoading = () => {
        return loadElement || <Loading />;
    };
    const tmpUrls = updateUrlParameter(onDealHaUrl(url), { micro_app_id: refAppKey.current });
    return (
        <div className={className}>
            {isLoading && renderLoading()}
            <micro-app
                style={isLoading ? { height: 0 } : style}
                router-mode="pure"
                level={5}
                destroy
                name={appName}
                url={tmpUrls}
                {...microProps}
                onCreated={() => onCommunication?.(MicroActType.CREATE_SUCCESS)}
                onBeforemount={() => onCommunication?.(MicroActType.BEFORE_MOUNT)}
                onMounted={onMounted}
                onUnmount={() => onCommunication?.(MicroActType.UN_MOUNT)}
                onError={() => onCommunication?.(MicroActType.ON_MOUNT_ERROR)}
            />
        </div>
    );
};
export { MicroEventKey, MicroAppContainer, IMicroAppContent };
